/**
 * Agent Helpers
 *
 * Shared utility functions used by agent tools and the spatial materializer.
 * Extracted to avoid circular dependencies.
 */

import type { TraceScene, SceneObject } from 'trace-viewer/scene';
import type { TraceViewerElement } from 'trace-viewer';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import { getUndoManager, CreateObjectCommand, resolveObjectRef, type CreateChildEntry } from 'trace-viewer/editor';
import type { CapturePose } from './capture-pose';
import type { AssistantPins } from './agent-types';

export interface ToolContext {
    traceScene: TraceScene;
    /**
     * Toolbar choices: templates are guides; style/set/wrapper picks override
     * the model's tool arguments. Absent = the model chooses a library guide.
     */
    assistantPins?: AssistantPins;
    /** The <trace-viewer> element — used by tools that need to call
     * element-level APIs (e.g. `import`). Tools should prefer this to
     * `document.querySelector('trace-viewer')` so that multiple viewers
     * on the page stay isolated and tests can inject a stub.
     *
     * Optional because headless contexts (mcp-server, mcp-bridge) run
     * without a DOM — tools that need the viewer must guard and fail
     * with a helpful error. */
    viewer?: TraceViewerElement;
    selectedObject: SceneObject | null;
    onSceneChanged?: () => void;
    /**
     * Declarative images queued by build_spatial's image() helper.
     * Generated in parallel by resolvePendingImages() after the agent loop —
     * the loop never blocks on image latency.
     */
    pendingImages?: { object: SceneObject; prompt: string }[];
    /**
     * Scene environment + key light authored by build_spatial's `environment()`
     * / `light()` helpers. The viewer reads its environment from `ConfigData`
     * at scene construction, not on mutation, so this is CARRIED rather than
     * applied: the host that builds the page (bench renderer, publish target)
     * sets the attributes before the element connects.
     */
    sceneEnvironment?: import('trace-viewer/build').SpatialEnvironmentDescriptor;
    /**
     * `instances()` adoption, accumulated across every `build_spatial` call in
     * a task (R4-C). Descriptive, like `sceneEnvironment`: the bench writes it
     * into `run.json` so "did the repetition construct actually get used?" is a
     * column rather than an argument about a contact sheet.
     */
    spatialInstances?: { calls: number; objects: number };
    /**
     * Live status from a long-running tool, shown on its chip in the chat
     * panel. 3D generation and the inference services run for minutes, and a
     * chip that says only "running" for that long is indistinguishable from a
     * hang. Absent in headless contexts (bench, MCP) — tools must treat it as
     * optional.
     */
    onToolProgress?: (status: string) => void;
    /** The turn's abort signal, so a tool's waits (a rate limit) stop on Stop. Absent outside a turn. */
    signal?: AbortSignal;
    /**
     * Where this scene is meant to be SEEN from — set by `focus_camera`, which
     * computes it arithmetically from bounds so it exists with or without a
     * render loop. Read by the headless `screenshot` path (render-bridge) and
     * by the bench, which writes it beside the exported scene so the capture
     * and the judge see the model's viewpoint rather than a fixed aerial.
     *
     * Per-context on purpose: bench tasks run concurrently, each with its own
     * scene, and a module-global would cross-contaminate them.
     */
    capturePose?: CapturePose | null;
}

/** Apply optional position/rotation/scale to a transform node. */
export function applyTransform(
    transform: import('@babylonjs/core/Meshes/transformNode').TransformNode,
    position?: [number, number, number],
    rotation?: [number, number, number],
    scale?: [number, number, number],
): void {
    if (position) {
        transform.position = new Vector3(position[0], position[1], position[2]);
    }
    if (rotation) {
        const degToRad = Math.PI / 180;
        transform.rotationQuaternion = Quaternion.FromEulerAngles(
            rotation[0] * degToRad,
            rotation[1] * degToRad,
            rotation[2] * degToRad,
        );
    }
    if (scale) {
        transform.scaling = new Vector3(scale[0], scale[1], scale[2]);
    }
    if (position || rotation || scale) {
        transform.computeWorldMatrix(true);
    }
}

/**
 * Look up a SceneObject by path or by name, failing loudly if the reference is
 * missing, ambiguous or disposed. Use this at boundaries where a string arrives
 * from outside the type-safe API (e.g. AI tool output) — internal code should
 * hold onto SceneObject references per the project's "no string targets" rule.
 *
 * Resolution is `trace-viewer/editor`'s `resolveObjectRef`: an exact path, then
 * a path suffix or bare name that matches exactly one object.
 *
 * @param kind Short label used in the error messages (e.g. 'clone source').
 * @throws If zero matches, more than one match, or a disposed match.
 */
export function resolveUniqueByName(
    traceScene: TraceScene,
    nameOrPath: string,
    kind: string,
): SceneObject {
    const result = resolveObjectRef(traceScene, typeof nameOrPath === 'string' ? nameOrPath : '');
    if ('object' in result) return result.object;
    if (result.error === 'NOT_FOUND') throw new Error(`${kind}: scene object "${nameOrPath}" not found.`);
    if (result.error === 'AMBIGUOUS') throw new Error(`${kind}: name ${result.message}`);
    throw new Error(`${kind}: ${result.message}`);
}

/**
 * Ground-align: offset so bottom of mesh sits at y=0.
 * Always applied — if the caller also passes an explicit position,
 * `applyTransform` will overwrite it afterwards.
 */
export function groundAlign(
    transform: import('@babylonjs/core/Meshes/transformNode').TransformNode,
): void {
    const { min } = transform.getHierarchyBoundingVectors(true);
    transform.position.y -= min.y;
}

/** Snapshot and push CreateObjectCommand */
export function recordCreateForUndo(
    obj: SceneObject,
    parent: SceneObject | null,
    childEntries: CreateChildEntry[] = [],
): void {
    if (getUndoManager().isUndoRedoing) return;
    obj.captureTransform();
    obj.refreshSceneData();
    getUndoManager().push(new CreateObjectCommand(
        obj,
        parent,
        obj.getSceneObjectData(),
        childEntries,
    ));
}

/**
 * A tool's object reference that names more than one object, or a disposed
 * one. `executeTool` turns it into the tool's `Error:` result, so the model
 * sees the candidate paths and retries with one.
 */
export class ObjectRefError extends Error {
    override name = 'ObjectRefError';
}

/**
 * Resolve an object reference from a tool call — "selected", a path, or a
 * name — to a SceneObject, by `trace-viewer/editor`'s `resolveObjectRef`.
 *
 * @returns null when nothing matches, so each tool keeps its own not-found
 *   result.
 * @throws ObjectRefError when the reference is ambiguous or disposed.
 */
export function resolveObject(objectRef: string, ctx: ToolContext): SceneObject | null {
    // Tool input is untyped at runtime: a missing or non-string ref is not found.
    if (typeof objectRef !== 'string') return null;
    if (objectRef === 'selected') return ctx.selectedObject;
    const result = resolveObjectRef(ctx.traceScene, objectRef);
    if ('object' in result) return result.object;
    if (result.error === 'NOT_FOUND') return null;
    throw new ObjectRefError(result.message);
}
